import type { SalesTaxReportJurisdiction } from '@/lib/services/reports/reports.types'
import { formatPercent, parseCurrency } from '@/lib/utils'

export interface JurisdictionChartRow extends SalesTaxReportJurisdiction {
  /** `tax_collected` as a number; negative when refunds outweigh sales. */
  tax_value: number
  chartKey: string
  chart_value: number
  fill: string | undefined
  share: number
}

export function buildJurisdictionChartRows(
  jurisdictions: SalesTaxReportJurisdiction[],
  colors: readonly string[]
): JurisdictionChartRow[] {
  const taxedRows = jurisdictions
    .map((jurisdiction) => ({
      ...jurisdiction,
      tax_value: parseCurrency(jurisdiction.tax_collected),
    }))
    .filter((row) => row.tax_value !== 0)
  const totalActivity = taxedRows.reduce((sum, row) => sum + Math.abs(row.tax_value), 0)

  if (totalActivity <= 0) {
    return []
  }

  return taxedRows.map((row, index) => ({
    ...row,
    chartKey: `jurisdiction-${index + 1}`,
    chart_value: Math.abs(row.tax_value),
    fill: colors[index % colors.length],
    share: Math.abs(row.tax_value) / totalActivity,
  }))
}

export function formatJurisdictionRate(rate: number): string {
  return formatPercent(rate, 2)
}

/** The rate, or the lowest to highest of the ascending `tax_rates` the API sends. */
export function formatRateSummary(rates: number[]): string {
  const lowest = rates[0]
  const highest = rates.at(-1)

  if (lowest === undefined || highest === undefined) {
    return '—'
  }

  if (lowest === highest) {
    return formatJurisdictionRate(lowest)
  }

  return `${formatJurisdictionRate(lowest)} – ${formatJurisdictionRate(highest)}`
}
